import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Typography,
  InputLabel,
  Box,
  Grid,
  Card,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  CircularProgress,
  Chip,
  Drawer,
  Divider,
  Tooltip,
  Popper,
  Fade
} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CreatableSelect from 'react-select/creatable';
import config from '../config';
import SupplementRow from './stack/SupplementRow';
import RelevanceLegend from './stack/RelevanceLegend';
import SafetyNote from './stack/SafetyNote';
import { orderByRelevance, rowKeys } from './stack/rowOrder';
import { citationRelevance, hasCitationRelevance } from '../utils/relevanceField';
import { color, r, radius } from '../theme/tokens';
import { useTheme } from '@mui/material/styles';
import { fetchSupplements } from '../Utils';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { searchProductsForSupplement, getProductById, findSupplements } from './products/amazonApi';
import StackProductCard from './products/StackProductCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import GroupIcon from '@mui/icons-material/Group';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SpeedIcon from '@mui/icons-material/Speed';
import { getAuth } from 'firebase/auth';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { formatFrequencyCompact, buildDosageTooltip, getDisplayDosage, applyEditedDose } from '../utils/dosageDisplay';
import { extractMedicationIngredients } from '../api/medications';
import { checkInteractionsForComponent, extractSupplementKeys } from '../utils/interactionChecker';
import { buttonStyles } from './common/ButtonStyles';
import { 
  groupInteractionsBySupplementKey, 
  createEnhancedInteractionData,
  formatInteractionSummary,
  getRiskEmoji
} from '../utils/interactionUtils';
import { getMedicationDisplayName } from '../utils/medicationUtils';
import InteractionDetailsModal from './common/InteractionDetailsModal';
import StackDisclaimer from './common/StackDisclaimer';
import useGuestInteractionCheck, { GUEST_CHECK_STATUS } from '../hooks/useGuestInteractionCheck';
import useLatestCheck from '../hooks/useLatestCheck';
import GuestMedicationsSummary from './guestMedications/GuestMedicationsSummary';
import GuestMembershipPanel from './guestMedications/GuestMembershipPanel';
import AnalysisNotes from './analyze/AnalysisNotes';
import { afterDoseEdit, analysisNoteFlags, hasAnalysis, rowAnalysisState } from './analyze/mergeAnalysis';
import { EMPTY_LIST_PROMPT, LIST_LOCKED_WHILE_ANALYZING } from './analyze/copy';
import { normalizeSupplementOption } from '../utils/supplementOption';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logAnalyticsEvent } from '../utils/analytics';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import WarningIcon from '@mui/icons-material/Warning';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { ScienceIcon, LocalPharmacyIcon } from '../icons';

const ProductCardContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1.5),
  height: '10rem', // 160px -> 10rem
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.8),
    height: '9.375rem', // 150px -> 9.375rem
  },
  [theme.breakpoints.up('md')]: {
    height: '10.625rem', // 170px -> 10.625rem
    padding: theme.spacing(1),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: '-0.625rem', // -10px -> -0.625rem
    width: '0.625rem', // 10px -> 0.625rem
    background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.03))',
    pointerEvents: 'none',
    zIndex: 2,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-0.625rem', // -10px -> -0.625rem
    width: '0.625rem', // 10px -> 0.625rem
    background: 'linear-gradient(to left, rgba(0,0,0,0), rgba(0,0,0,0.03))',
    pointerEvents: 'none',
    zIndex: 2,
  },
}));

const ProductCarouselControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(1),
}));

const ProductCountIndicator = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem', // 12px -> 0.75rem
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  '& .dot': {
    height: '0.375rem', // 6px -> 0.375rem
    width: '0.375rem', // 6px -> 0.375rem
    borderRadius: '50%',
    display: 'inline-block',
    margin: '0 0.25rem', // 0 4px -> 0.25rem
  },
  '& .active': {
    backgroundColor: theme.palette.primary.main,
  },
  '& .inactive': {
    backgroundColor: theme.palette.action.disabled,
  },
}));

const ProductOverlayButton = styled(IconButton)(({ theme, direction }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 5,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 0.0625rem 0.1875rem rgba(0,0,0,0.2)', // 1px 3px -> 0.0625rem 0.1875rem
  padding: '0.375rem', // 6px -> 0.375rem
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  ...(direction === 'left' ? {
    left: '-0.5rem', // -8px -> -0.5rem
  } : {
    right: '-0.5rem', // -8px -> -0.5rem
  }),
  [theme.breakpoints.down('sm')]: {
    padding: '0.25rem', // 4px -> 0.25rem
  }
}));

const ProductCountBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '0.5rem', // 8px -> 0.5rem
  left: '0.5rem', // 8px -> 0.5rem
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  borderRadius: '0.75rem', // 12px -> 0.75rem
  padding: '0.125rem 0.5rem', // 2px 8px -> 0.125rem 0.5rem
  fontSize: '0.7rem', // 11.2px -> 0.7rem (approx)
  fontWeight: 500,
  zIndex: 5,
  boxShadow: '0 0.0625rem 0.1875rem rgba(0,0,0,0.2)', // 1px 3px -> 0.0625rem 0.1875rem
}));

// What a members' interaction check is for: each supplement, its dose, and
// whether it is in the stack. Interaction data is left out, so writing a
// check's answer into the rows does not start another check.
const interactionCheckKey = (supplements) => supplements
  .map((s) => `${s.supplementId || s.key}-${s.dosage}-${s.unit}-${s.selected !== false ? '1' : '0'}`)
  .sort()
  .join('|');

const SupplementsTable = ({
  selectedSupplements,
  setSelectedSupplements,
  citations,
  getStackId,
  handleMainLinkClick,
  handleDrawerLinkClick,
  pendingDrawerRestoreInfo,
  onDrawerRestored,
  stackVersion,
  readOnly = false,
  // Set when the table is rendered inside a Dialog (the dashboard's
  // ViewStackModal). The research drawer defaults to zIndex.drawer, which is
  // below zIndex.modal, so without this it opens behind that dialog.
  inDialog = false,
  alwaysCheckInteractions = false, // Allow bypassing Pro access check (e.g., after stack creation)
  hideAddButton = false, // Hide the add supplement button (for curated stacks)
  // `hidePersonalizeBanner` was removed with the banner itself — see the note
  // at the old render site below.
  isCuratedStack = false, // Show different customization note for curated stacks
  curatedGoalName = null, // Goal name for curated stack personalize link
  // Suppress the per-stack disclaimer. Set by list surfaces that render
  // several stacks and show one disclaimer for the whole view.
  hideDisclaimer = false,
  /**
   * Render Analyze Current Stack's results on this stack (STACK-544): the
   * member's amount and the study's as two chips, the Add amount / Check
   * amount row actions, and the muted lines under the list.
   *
   * A flag rather than a second table. Everything else about the stack —
   * interaction checking, the drawer, the product panel, the disclaimer —
   * is identical, and a second renderer is how two surfaces stop agreeing
   * about what a stack looks like.
   *
   * It is *permission*, not instruction: the rows still have to carry
   * analysis for anything to change (`hasAnalysis`), so a stack of this
   * origin that has never been analysed renders exactly as any other.
   */
  showAnalysis = false,
  /**
   * An analysis of this list is running. The list is locked until it
   * finishes — no adding, removing, switching off or dose edits — and every
   * row says it is being analysed. A supplement added mid-run used to be left
   * out silently and only reported afterwards (staging case C4, 2026-10-02).
   */
  analyzing = false,
  /**
   * A logged-out visitor's medication check (STACK-565):
   * `{ healthGoals, medications, generatedKeys }` from CreateStack, or null.
   * Null — every member surface — leaves this table exactly as it was.
   */
  guestCheck = null,
}) => {
  const { hasAccess } = useFeatureAccess();
  const guest = useGuestInteractionCheck(guestCheck, selectedSupplements);

  // Permission AND payload. `showAnalysis` says the surface may render
  // results; `hasAnalysis` says there are any. A stack created by the analyze
  // flow but never analysed renders exactly like any other stack.
  const analysisVisible = showAnalysis && hasAnalysis(selectedSupplements);
  const noteFlags = analysisVisible
    ? analysisNoteFlags(selectedSupplements)
    : { hasMerged: false, hasNotInLibrary: false, hasNoStudies: false };
  
  // Validate required imports
  if (!MedicalServicesIcon) {
    console.error('Required icons not imported properly');
  }

  const [newSupplement, setNewSupplement] = useState(null);
  const [newDosage, setNewDosage] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [supplementOptions, setSupplementOptions] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const theme = useTheme();

  const [supplementProducts, setSupplementProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});
  const [activeStep, setActiveStep] = useState({});
  
  const [expandedProducts, setExpandedProducts] = useState({});
  
  // key -> product_count. A Map rather than a Set because /supplements/find
  // already returns the count, and a row can then say "8 products" before any
  // product fetch happens.
  const [supplementsWithProducts, setSupplementsWithProducts] = useState(new Map());
  
  // Interaction modal state
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [selectedInteractionData, setSelectedInteractionData] = useState(null);
  const [fetchedMedicationsForInteractions, setFetchedMedicationsForInteractions] = useState([]);
  const [loadingSupplementList, setLoadingSupplementList] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSupplementName, setDrawerSupplementName] = useState('');
  const [drawerCitations, setDrawerCitations] = useState([]);
  const [drawerSupplementId, setDrawerSupplementId] = useState(null);
  const [savedDrawerScrollPosition, setSavedDrawerScrollPosition] = useState(0);
  const drawerContentRef = useRef(null);
  
  // Mobile-friendly interaction tooltip state
  
  // Refs for interaction tooltips (needed for Popper anchoring)

  // Interaction checking state
  const [isCheckingInteractions, setIsCheckingInteractions] = useState(false);
  const [interactionCheckError, setInteractionCheckError] = useState(null);
  const [userMedications, setUserMedications] = useState([]);
  // Only the check for the current selection may write to it.
  const interactionCheck = useLatestCheck();

  const showAffiliateStore = config.FEATURES.AFFILIATE_STORE;

  const navigate = useNavigate();

  // Helper function to fetch user medications from Firestore
  const fetchUserMedications = async (userId) => {
    try {
      const medProfileRef = doc(db, 'med_profile', userId);
      const medProfileDoc = await getDoc(medProfileRef);
      
      if (medProfileDoc.exists()) {
        const medProfileData = medProfileDoc.data();
        return medProfileData.medications || [];
      }
      return [];
    } catch (error) {
      console.error('[SupplementsTable] Error fetching user medications:', error);
      return [];
    }
  };

  // Helper function to extract supplement keys from current supplements



  // Function to check interactions for current supplements. Every write
  // waits on `signal`: an aborted check is for rows the member has changed.
  const checkSupplementInteractions = async (signal) => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user || !selectedSupplements || selectedSupplements.length === 0) {
      return;
    }

    // Onto the rows as they are when the answer lands, and only while they
    // are still the rows this check is for. The form may have written to them
    // meanwhile without changing the selection (an analysis folds its results
    // in), and an answer can land after the selection changed but before the
    // effect that cancels this check has run.
    const checkedKey = interactionCheckKey(selectedSupplements);
    const setInteractions = (interactionFor) => setSelectedSupplements((current) => (
      Array.isArray(current) && interactionCheckKey(current) === checkedKey
        ? current.map((supplement) => ({ ...supplement, interaction: interactionFor(supplement) }))
        : current
    ));

    // Check if user has access to interaction checking (unless alwaysCheckInteractions is true)
    if (!hasAccess('interaction_checking') && !alwaysCheckInteractions) {
      return;
    }

    try {
      // Fetch user medications
      const fetchedMedications = await fetchUserMedications(user.uid);
      if (signal.aborted) return;
      
      // Store medications in state for tooltip mapping and modal display
      setUserMedications(fetchedMedications);
      setFetchedMedicationsForInteractions(fetchedMedications);
      
      if (fetchedMedications.length === 0) {
        // Clear any existing interaction data
        setInteractions(() => ({
          has_interaction: false,
          risk: null,
          reasoning: 'No medications on file',
          interacting_medication: null
        }));
        return;
      }
      
      // Extract supplement keys and medication ingredients (only for selected/included supplements)
      const includedSupplements = selectedSupplements.filter(s => s.selected !== false);
      
      if (includedSupplements.length === 0) {
        // No supplements included, clear interaction data
        return;
      }
      
      const supplementKeys = extractSupplementKeys(includedSupplements);
      const medicationIngredients = extractMedicationIngredients(fetchedMedications);
      
      if (supplementKeys.length === 0 || medicationIngredients.length === 0) {
        return;
      }
      
      // Get Firebase Auth token
      const authToken = await user.getIdToken();
      if (signal.aborted) return;

      // Use the new utility with retry logic
      await checkInteractionsForComponent(
        medicationIngredients,
        supplementKeys,
        authToken,
        {
          signal,
          setIsChecking: setIsCheckingInteractions,
          setError: setInteractionCheckError,
          onProcessing: (processingResponse) => {
            // Keep interaction check as in progress during processing
          },
          onComplete: (finalResponse) => {
            if (finalResponse.interactions && finalResponse.interactions.length > 0) {
              // Group interactions by supplement key to handle multiple interactions per supplement
              const interactionsBySupplementKey = groupInteractionsBySupplementKey(finalResponse.interactions);
              
              // Update supplements with enhanced interaction data, with fetched medications
              setInteractions((supplement) => createEnhancedInteractionData(
                interactionsBySupplementKey[supplement.supplementId || supplement.key] || [],
                (rxcui) => getMedicationDisplayName(rxcui, fetchedMedications)
              ));
            } else {
              // No interactions found
              setInteractions(() => createEnhancedInteractionData([])); // Empty array = no interactions
            }
          }
        }
      );
      
    } catch (error) {
      if (signal.aborted) return;
      console.error('[SupplementsTable] Error checking supplement interactions:', error);
      
      // Update supplements with error state
      setInteractions(() => ({
        has_interaction: false,
        risk: null,
        reasoning: 'Error checking interactions',
        interacting_medication: null,
        error: true
      }));
    }
  };

  // Function to handle opening interaction details modal
  const handleInteractionClick = (supplement) => {
    if (!supplement.interaction || !supplement.interaction.has_interaction) {
      return;
    }

    // Ensure medication names are properly resolved for modal display
    const interactionsWithNames = (supplement.interaction.allInteractions || []).map(interaction => ({
      ...interaction,
      medicationName: interaction.medicationName || getMedicationDisplayName(interaction.medication_in_rxcui, fetchedMedicationsForInteractions)
    }));

    const interactionData = {
      supplementName: supplement.name,
      interactions: interactionsWithNames,
      primaryInteraction: supplement.interaction.primaryInteraction || supplement.interaction
    };

    setSelectedInteractionData(interactionData);
    setInteractionModalOpen(true);
  };

  // Function to create enhanced tooltip content for multiple interactions

  // Load user medications when component mounts
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      fetchUserMedications(user.uid)
        .then(medications => {
          setUserMedications(medications);
        })
        .catch(error => {
          console.error('[SupplementsTable] Error loading medications on mount:', error);
        });
    }
  }, []); // Run once on mount

  // Check for interactions when supplements change
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Check access inside the effect to avoid function dependency issues
    const hasInteractionAccess = hasAccess('interaction_checking');
    
    // Check if we have a user, supplements, and Pro access
    // OR if we're in readOnly mode (ViewStackModal should always check)
    // OR if alwaysCheckInteractions is true (e.g., after stack creation in CreateStack)
    if (user && selectedSupplements && selectedSupplements.length > 0 && (hasInteractionAccess || readOnly || alwaysCheckInteractions)) {
      // Check if this supplement combination hasn't been checked yet. A new
      // one stops the check before it.
      const signal = interactionCheck.startIfNew(interactionCheckKey(selectedSupplements));
      
      if (signal) {
        // Call immediately without timeout to avoid race conditions with rapid re-renders
        checkSupplementInteractions(signal).then(() => {
          // However the newest check ended, before asking or after, it is not
          // checking. One it replaced leaves the spinner as it was until then.
          if (!signal.aborted) setIsCheckingInteractions(false);
        });
      }
    } else {
      // Nothing to check (no supplements, signed out, or no access): stop any
      // check still running, and clear its state
      interactionCheck.cancel();
      setIsCheckingInteractions(false);
      setInteractionCheckError(null);
    }
  }, [selectedSupplements, readOnly, alwaysCheckInteractions]); // Include all relevant dependencies

  useEffect(() => {
    const loadSupplementsWithProducts = async () => {
      try {
        const { supplements } = await findSupplements({ sort_by: 'products' });
        setSupplementsWithProducts(
          new Map(supplements.map((s) => [s.key, s.product_count])),
        );
      } catch (error) {
        console.error('Error loading supplements with products:', error);
      } finally {
        setLoadingSupplementList(false);
      }
    };

    loadSupplementsWithProducts();
  }, []);

  useEffect(() => {
    const loadSupplements = async () => {
      try {
        const supplementsList = await fetchSupplements(config);
        
        if (!Array.isArray(supplementsList)) {
          console.error('[SupplementsTable] Fetched data is not an array:', supplementsList);
          setSupplementOptions([]);
          return;
        }

        const options = supplementsList.map((supplement) => ({
          label: supplement.name,
          value: supplement.key,
          hasProducts: supplementsWithProducts.has(supplement.key)
        }));
        
        options.sort((a, b) => a.label.localeCompare(b.label));
        
        setSupplementOptions(options);
      } catch (error) {
        console.error('[SupplementsTable] Error loading supplements:', error);
        setSupplementOptions([]);
      }
    };

    if (!loadingSupplementList) {
      loadSupplements();
    }
  }, [loadingSupplementList, supplementsWithProducts]);

  const fetchProductsForSupplement = async (supplement) => {
    if (!supplement) return;
    if (supplementProducts[supplement.name] || loadingProducts[supplement.name]) return;
    
    setLoadingProducts(prev => ({ ...prev, [supplement.name]: true }));
    
    try {
      const products = await searchProductsForSupplement(supplement.supplementId);
      setSupplementProducts(prev => ({
        ...prev,
        [supplement.name]: products
      }));
      setActiveStep(prev => ({
        ...prev,
        [supplement.name]: 0
      }));
    } catch (error) {
      console.error(`Error fetching products for ${supplement.name}:`, error);
    } finally {
      setLoadingProducts(prev => ({ ...prev, [supplement.name]: false }));
    }
  };

  const handleAddSupplement = async () => {
    if (!newSupplement || !newDosage || !newUnit) return;

    let descriptionContent = 'Description unavailable.'; 

    try {
      const response = await axios.get(
        `${config.FIREBASE_STORAGE_BASE_URL}supplements%2F${newSupplement.value}.json?alt=media`
      );
      descriptionContent = response.data?.note?.content ||
                           response.data?.['note:']?.content ||
                           'No description available';
    } catch (error) {
      console.error(`Error fetching description for ${newSupplement.label}:`, error);
    }

    const newSupplementItem = {
      name: newSupplement.label,
      supplementId: newSupplement.value,
      dosage: newDosage,
      unit: newUnit,
      description: descriptionContent, 
      evidenceScore: null, 
      study_count: 0,
    };

    setSelectedSupplements([...selectedSupplements, newSupplementItem]);

    setAddModalOpen(false);
    setNewSupplement(null);
    setNewDosage('');
    setNewUnit('');

    if (showAffiliateStore) {
      fetchProductsForSupplement(newSupplementItem);
    }
  };

  // Commit an inline dose edit. Replaces the three-dot menu → modal dialog
  // path (STACK-480 ruling, 2026-08-12): the dose chip is now the editor, so
  // this writes straight into the working stack and the parent form's Save
  // persists it, exactly as the dialog's Save did. Field sync for
  // pdf_verified per-dose rows lives in applyEditedDose.
  const handleDoseChange = (index, dosage, unit) => {
    const updatedSupplements = [...selectedSupplements];
    // `afterDoseEdit` drops what an analysis said about the old amount (the
    // Add / Change amount notice, "combined") when the amount really changed;
    // a no-op on unanalysed rows and on a commit that changed nothing.
    const previous = updatedSupplements[index];
    updatedSupplements[index] = afterDoseEdit(applyEditedDose(
      previous,
      dosage,
      unit,
    ), previous);
    setSelectedSupplements(updatedSupplements);
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
  };

  const handleToggleSelect = (index) => {
    const updatedSupplements = [...selectedSupplements];
    const isSelected = updatedSupplements[index].selected !== false;
    updatedSupplements[index] = {
      ...updatedSupplements[index],
      selected: !isSelected
    };
    setSelectedSupplements(updatedSupplements);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewSupplement(null);
    setNewDosage('');
    setNewUnit('');
  };

  const getSupplementDisplayName = (supplement) => {
    if (!supplement) return 'Unknown supplement';
    
    if (supplement.name) {
      return supplement.name;
    }

    return supplement.key || supplement.supplementId || 'Unknown supplement';
  };


  const handleViewResearchClick = (supplement) => {
    const supplementId = supplement.supplementId;
    
    // Always open drawer even if no citations
    const newState = { myStackDrawerOpen: true, supplementId: supplementId };
    
    // Replace state if drawer is already open to avoid stacking history entries
    if (drawerOpen) {
      window.history.replaceState(newState, '');
    } else {
      window.history.pushState(newState, '');
    }
    
    // Set citations if available, or empty array if not
    const citationsForSupplement = citations?.[supplementId] || [];
    setDrawerCitations(citationsForSupplement);
    setDrawerSupplementName(getSupplementDisplayName(supplement));
    setDrawerSupplementId(supplementId);
    setDrawerOpen(true);

    // Log the event
    const supplementName = getSupplementDisplayName(supplement);
    logAnalyticsEvent('click_view_research', {
      item_id: supplementId,
      item_name: supplementName,
      item_category: 'supplement',
      item_list_context: 'stack_supplements_table',
      study_count: citationsForSupplement.length
    });
  };

  // Update the handleCloseDrawer function to also handle history
  const handleCloseDrawer = (triggeredByHistory = false) => {
    if (!drawerOpen) return;
    
    // Only modify history if NOT triggered by popstate event
    if (!triggeredByHistory) {
      // Replace the current state with one that doesn't have the drawer open
      window.history.replaceState({ myStackDrawerOpen: false }, '');
    }
    
    setDrawerOpen(false);
  };

  // Improve the handlePopState function inside useEffect
  useEffect(() => {
    const handlePopState = (event) => {
      const targetHistoryState = event.state;
      
      // When navigating back
      if (!targetHistoryState || targetHistoryState.myStackDrawerOpen === false) {
        // If drawer is open, close it
        if (drawerOpen) {
          handleCloseDrawer(true);
        }
        return;
      }
      
      // When navigating to a drawer state
      if (targetHistoryState.myStackDrawerOpen === true) {
        const targetSupplementId = targetHistoryState.supplementId;
        
        // If supplementId is different or drawer is closed, update drawer content
        if (!drawerOpen || targetSupplementId !== drawerSupplementId) {
          const supplement = selectedSupplements.find(s => s.supplementId === targetSupplementId);
          const citationsForSupplement = citations?.[targetSupplementId];
          
          if (supplement && citationsForSupplement && citationsForSupplement.length > 0) {
            setDrawerCitations(citationsForSupplement);
            setDrawerSupplementName(getSupplementDisplayName(supplement));
            setDrawerSupplementId(targetSupplementId);
            setSavedDrawerScrollPosition(0);
            setDrawerOpen(true);
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [drawerOpen, drawerSupplementId, selectedSupplements, citations]);

  useEffect(() => {
    if (pendingDrawerRestoreInfo && citations && selectedSupplements.length > 0) {
      const { supplementId, scroll } = pendingDrawerRestoreInfo;
      const supplement = selectedSupplements.find(s => s.supplementId === supplementId);
      const citationsForSupplement = citations?.[supplementId];

      if (supplement && citationsForSupplement && citationsForSupplement.length > 0) {
        const currentState = window.history.state;
        if (!(currentState?.myStackDrawerOpen && currentState?.supplementId === supplementId)) {
           window.history.pushState({ myStackDrawerOpen: true, supplementId: supplementId }, '');
        }

        setDrawerCitations(citationsForSupplement);
        setDrawerSupplementName(getSupplementDisplayName(supplement));
        setDrawerSupplementId(supplementId);
        setSavedDrawerScrollPosition(scroll || 0);
        setDrawerOpen(true);

        onDrawerRestored();
      } else {
        onDrawerRestored();
      }
    }
  }, [pendingDrawerRestoreInfo, citations, selectedSupplements]);

  useEffect(() => {
    if (drawerOpen && drawerContentRef.current && savedDrawerScrollPosition > 0) {
      const scrollTarget = savedDrawerScrollPosition;
      const timer = setTimeout(() => {
         if (drawerContentRef.current) {
            drawerContentRef.current.scrollTop = scrollTarget;
            setSavedDrawerScrollPosition(0);
         }
      }, 150);

      return () => clearTimeout(timer);
    } else if (drawerOpen && savedDrawerScrollPosition === 0 && drawerContentRef.current) {
       drawerContentRef.current.scrollTop = 0;
    }
  }, [drawerOpen, savedDrawerScrollPosition]);

  const handleNext = (supplementName) => {
    setActiveStep(prev => ({
      ...prev,
      [supplementName]: Math.min(prev[supplementName] + 1, (supplementProducts[supplementName]?.length || 1) - 1)
    }));
  };

  const handleBack = (supplementName) => {
    setActiveStep(prev => ({
      ...prev,
      [supplementName]: Math.max(prev[supplementName] - 1, 0)
    }));
  };

  const handleViewProductsClick = async (supplement) => {
    if (!showAffiliateStore) return;
    
    const isCurrentlyExpanded = !!expandedProducts[supplement.name];
    const newState = !isCurrentlyExpanded;

    // Log the event
    logAnalyticsEvent('click_view_products_stack', {
      item_id: supplement.supplementId,
      item_name: getSupplementDisplayName(supplement),
      item_category: 'supplement',
      item_list_context: 'stack_supplements_table',
      expanded: newState // Track if expanding or collapsing
    });

    setExpandedProducts(prev => ({
      ...prev,
      [supplement.name]: newState
    }));
    
    if (newState && !supplementProducts[supplement.name] && !loadingProducts[supplement.name]) {
      await fetchProductsForSupplement(supplement);
    }
  };

  const hasProducts = (supplement) => {
    return showAffiliateStore && supplementsWithProducts.has(supplement.supplementId);
  };

  const handleDrawerArticleLinkClickInternal = (articleId, e) => {
     e.preventDefault();
     if (!articleId || !drawerSupplementId) return;

     const drawerScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
     handleDrawerLinkClick(articleId, drawerSupplementId, drawerScroll);
  };

  const scoreChipStyle = {
    height: '1.375rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    color: theme.palette.primary.dark,
    borderColor: theme.palette.primary.light,
    bgcolor: color.mintSoft,
    '& .MuiChip-icon': {
       fontSize: '1rem !important',
       marginLeft: '0.25rem',
       marginRight: '-0.125rem',
     }
  };

  const attributeChipStyle = {
    height: '1.25rem',
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    borderColor: theme.palette.grey[300],
    bgcolor: theme.palette.grey[50],
     '& .MuiChip-icon': {
       fontSize: '0.9rem !important',
       marginLeft: '0.1875rem',
       marginRight: '-0.25rem',
     }
  };

  const transformArticleRefs = (text, supplementId) => {
    if (!text) return 'No information available';

    text = text.replace(/<\/article_ref>\./g, '</article_ref>'); 
    const parts = text.split(/(<article_ref>.*?<\/article_ref>)/);
    
    return parts.map((part, index) => {
      const match = part.match(/<article_ref>(.*?<\/article_ref>)/);
      if (match) {
        const citation = match[1];
        const refId = citation.split(' ')[0].replace('</article_ref>', '');
        
        return (
          <IconButton
            key={index}
            size="small"
            onClick={(e) => {
              e.preventDefault();
              handleDrawerLinkClick(refId, supplementId, 0);
            }}
            sx={{
              padding: '0',
              color: 'primary.light',
              minWidth: 'auto',
              ml: 0.5,
              transform: 'translateY(-0.0625rem)', // -1px -> -0.0625rem
              opacity: 0.7,
              backgroundColor: 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 1,
                transform: 'translateY(-0.125rem)', // -2px -> -0.125rem
                color: 'primary.main',
              },
              '@media (max-width: 37.5rem)': { // 600px -> 37.5rem
                padding: '0.25rem', // 4px -> 0.25rem
              }
            }}
            title="View research study"
          >
            <DescriptionIcon sx={{ fontSize: '1.0rem' }} />
          </IconButton>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const showDosageFootnote = selectedSupplements.some(
    supplement => supplement.dosageSource === 'study' || supplement.dosageSource === 'pdf_verified'
  );

  // The logged-out personalization prompt used to live here, between the rows
  // and the add button. It is gone entirely (Tanu, 2026-08-14): the relevance
  // banner at the top of the list now carries both halves of it — "based on
  // general research" and the "Log in" call to action — so repeating them at
  // the bottom said the same thing twice.

  // Let's add a check to ensure citations are properly structured

  // Display order only — see `rowOrder.js` for why the array itself is never
  // reordered.
  const rowOrder = orderByRelevance(selectedSupplements);
  // React keys by index in `selectedSupplements` — see `rowKeys`.
  const keys = rowKeys(selectedSupplements);

  return (
    <>
      {guest.active && (
        <GuestMedicationsSummary
          medications={guest.medications}
          status={guest.status}
          flagCount={guest.flagCount}
          checkedCount={guest.checkedCount}
          onRetry={guest.retry}
        />
      )}

      {/* Interaction checking indicator — members only. A logged-out visitor's
          check reports in the Your medications panel's status line, which is
          the only one (Aravind, 2026-10-08). */}
      {isCheckingInteractions && !guest.active && (
        <Box sx={{ 
          mt: 1, 
          mb: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 1,
          p: 1,
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          borderRadius: 1,
          border: '1px solid rgba(25, 118, 210, 0.12)'
        }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="primary" sx={{ fontSize: '0.875rem' }}>
            Checking for medication interactions...
          </Typography>
        </Box>
      )}

      {selectedSupplements.length > 0 && (
        <RelevanceLegend isCuratedStack={isCuratedStack} curatedGoalName={curatedGoalName} />
      )}

      <Box
        sx={{
          marginTop: r(16),
          border: `${r(1)} solid ${color.line}`,
          borderRadius: radius.lg,
          backgroundColor: color.paper,
          overflow: 'hidden',
        }}
      >
        {rowOrder.length > 0 ? (
          rowOrder.flatMap(({ supplement, index }, position) => {
            const isSelected = supplement.selected !== false;
            const rows = [
              <SupplementRow
                key={keys[index]}
                // A guest check's flags are attached here, at render, and
                // never written into the stack's state (and so never into
                // its localStorage snapshot). Members' rows pass through.
                supplement={guest.decorate(supplement)}
                guestCheckChip={guest.rowChip(supplement)}
                isFirst={position === 0}
                readOnly={readOnly || analyzing}
                isSelected={isSelected}
                displayName={getSupplementDisplayName(supplement)}
                description={
                  (supplement.description || supplement.note)
                    ? transformArticleRefs(supplement.description || supplement.note, supplement.supplementId)
                    : null
                }
                studyCount={(citations && citations[supplement.supplementId]?.length) || 0}
                studiesExpanded={drawerOpen && drawerSupplementId === supplement.supplementId}
                showProducts={hasProducts(supplement)}
                productCount={
                      supplementProducts[supplement.name]?.length
                      ?? supplementsWithProducts.get(supplement.supplementId)
                    }
                productsExpanded={expandedProducts[supplement.name]}
                productsBusy={loadingProducts[supplement.name]}
                onNameClick={(e) => handleMainLinkClick(supplement.supplementId, e)}
                onDoseChange={({ dosage, unit }) => handleDoseChange(index, dosage, unit)}
                onStudiesClick={() => handleViewResearchClick(supplement)}
                onProductsClick={() => handleViewProductsClick(supplement)}
                onRiskClick={() => handleInteractionClick(guest.decorate(supplement))}
                onToggleSelect={() => handleToggleSelect(index)}
                showAnalysis={analysisVisible}
                analysisState={showAnalysis
                  ? rowAnalysisState(supplement, { analyzing, listAnalysed: analysisVisible })
                  : null}
              />
            ];
          
            if (showAffiliateStore && expandedProducts[supplement.name]) {
              rows.push(
                <Box key={`product-${keys[index]}`} sx={{
                  backgroundColor: color.surface,
                  opacity: isSelected ? 1 : 0.4, // Match supplement row opacity
                  transition: 'all 0.3s ease',
                  padding: `${r(12)} ${r(18)}`,
                  borderTop: `${r(1)} solid ${color.lineSoft}`,
                }}>
                    {loadingProducts[supplement.name] ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={1.5} color="primary" />
                      </Box>
                    ) : supplementProducts[supplement.name]?.length > 0 ? (
                      <Box sx={{ 
                        position: 'relative',
                        maxWidth: '100%',
                        height: '8.75rem', // 140px
                        borderRadius: '0.5rem', // 8px
                        overflow: 'visible',
                      }}>
                        {(activeStep[supplement.name] || 0) > 0 && (
                          <ProductOverlayButton 
                            direction="left"
                            onClick={() => handleBack(supplement.name)}
                            size="small"
                            aria-label="previous product"
                          >
                            <KeyboardArrowLeft />
                          </ProductOverlayButton>
                        )}
                      
                        {(activeStep[supplement.name] || 0) < supplementProducts[supplement.name].length - 1 && (
                          <ProductOverlayButton 
                            direction="right"
                            onClick={() => handleNext(supplement.name)}
                            size="small"
                            aria-label="next product"
                          >
                            <KeyboardArrowRight />
                          </ProductOverlayButton>
                        )}
                      
                        {supplementProducts[supplement.name].length > 1 && (
                          <ProductCountBadge>
                            {(activeStep[supplement.name] || 0) + 1}/{supplementProducts[supplement.name].length}
                          </ProductCountBadge>
                        )}
                      
                        <Box sx={{ height: '100%', width: '100%' }}>
                          <StackProductCard 
                            product={{
                              ...supplementProducts[supplement.name][activeStep[supplement.name] || 0],
                              sourceSupplement: supplement.name,
                              supplementKey: supplement.supplementId
                            }} 
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        // Was a hardcoded `white` on a `rgba(0,0,0,0.1)` dash.
                        // The literals predate this phase, but the panel around
                        // them is now `color.surface`, so a raw white no longer
                        // sits on the ground it was picked against.
                        backgroundColor: color.paper,
                        borderRadius: radius.sm,
                        border: `${r(1)} dashed ${color.line}`,
                        height: r(100),
                        justifyContent: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          textAlign: 'center',
                          fontWeight: 500
                        }}>
                          No products found for {supplement.name}
                        </Typography>
                      </Box>
                    )}
                </Box>
              );
            }

            return rows;
          })
        ) : (
          // On a phone the prompt fits one line at 14px with 16px side padding
          // (measured: 290px of text in 309px at 393 wide, 328px at 412),
          // where at 16px "dose." wrapped alone onto a second line (Tanu's
          // staging test, case S4). Balanced wrapping covers anything narrower.
          <Box sx={{ padding: { xs: `${r(24)} ${r(16)}`, sm: r(24) }, textAlign: 'center' }}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ fontSize: { xs: r(14), sm: r(16) }, textWrap: 'balance' }}
            >
              {/* The analysis surface says what to enter (handoff,
                  2026-09-24); every other stack keeps its line. Keyed on
                  `showAnalysis`, not `analysisVisible`: an empty list has no
                  results yet, and this is exactly when the prompt is needed. */}
              {showAnalysis ? EMPTY_LIST_PROMPT : 'No supplements added yet.'}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ 
         mt: 0.75, 
         pl: 0.5,
         display: 'flex', 
         flexDirection: 'column',
         gap: 0.5 
      }}>
        {showDosageFootnote && (
           <Typography 
             variant="caption" 
             sx={{ 
               color: 'text.secondary', 
               fontSize: '0.65rem', // 10.4px
               display: 'flex', 
               alignItems: 'center', 
               gap: 0.3 
             }}
           >
             <InfoOutlinedIcon sx={{ fontSize: '0.75rem' }} />
             Evidence-based dosage
           </Typography>
        )}
      </Box>

      {selectedSupplements.length > 0 && (
        <SafetyNote
          isLoggedIn={!!getAuth().currentUser}
          hasPrescriptions={userMedications.length > 0}
          hasFlags={selectedSupplements.some((s) => s?.interaction?.has_interaction)}
          checking={isCheckingInteractions}
          checkError={interactionCheckError}
          guestStatus={guest.active ? guest.status : null}
        />
      )}

      {/* The rest of STACK-544's muted slot, in its ruled order: the
          interaction line above (unchanged by that ticket), then these.
          Renders nothing at all when no condition holds. */}
      {analysisVisible && (
        <AnalysisNotes
          hasMerged={noteFlags.hasMerged}
          hasNotInLibrary={noteFlags.hasNotInLibrary}
          mergedNames={noteFlags.mergedNames}
          notInLibraryNames={noteFlags.notInLibraryNames}
          hasNoStudies={noteFlags.hasNoStudies}
          noStudiesNames={noteFlags.noStudiesNames}
        />
      )}

      {/* The add control, per handoff frame 2: full width, centred, dashed —
          a target, not a floating circle whose purpose you have to infer. */}
      {!readOnly && !hideAddButton && analyzing && (
        <Box
          data-testid="list-locked-hint"
          role="status"
          sx={{
            mt: r(12),
            padding: r(13),
            textAlign: 'center',
            border: `${r(1.5)} dashed ${color.line}`,
            borderRadius: radius.lg,
            fontSize: r(13),
            color: color.inkMuted,
          }}
        >
          {LIST_LOCKED_WHILE_ANALYZING}
        </Box>
      )}
      {!readOnly && !hideAddButton && !analyzing && (
        <ButtonBase
          onClick={handleOpenAddModal}
          sx={{
            mt: r(12),
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: r(8),
            padding: r(13),
            border: `${r(1.5)} dashed ${color.line}`,
            borderRadius: radius.lg,
            backgroundColor: 'transparent',
            fontSize: r(14),
            fontWeight: 700,
            color: color.evergreen,
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: color.accent,
              color: color.accent,
              backgroundColor: color.surface,
            },
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ fontSize: r(17), lineHeight: 1, color: color.accent }}>+</Box>
          Add a supplement
        </ButtonBase>
      )}

      {/* Legal copy travels with the stack, not with any one page, so a caller
          cannot omit it by forgetting. This table is one of two components that
          render a stack — StackCard is the other, and carries its own copy of
          this. Suppressed when there is nothing to disclaim (empty table), or
          when a list surface shows one disclaimer for several stacks.
          See STACK-517. */}
      {!hideDisclaimer && selectedSupplements.length > 0 && <StackDisclaimer />}

      {/* Under the result, once there is one, so the warnings are read first. */}
      {guest.active
        && [GUEST_CHECK_STATUS.FLAGS, GUEST_CHECK_STATUS.CLEAN, GUEST_CHECK_STATUS.ERROR].includes(guest.status)
        && <GuestMembershipPanel />}

      <Dialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            boxShadow: '0 0.5rem 2rem -0.5rem rgba(0,0,0,0.2)', // 8px 32px 8px
            margin: { xs: '1rem', sm: '2rem' },
            position: 'absolute',
            top: { xs: 0, sm: '50%' },
            transform: { 
              xs: 'none',
              sm: 'translateY(-50%)'
            },
            maxHeight: { 
              xs: 'calc(100% - 2rem)', // 32px
              sm: 'none' 
            },
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: { xs: 'flex-start', sm: 'center' }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <AddIcon color="primary" />
          <Typography variant="h6" component="span" color="primary">
            Add Supplement
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseAddModal}
            sx={{
              position: 'absolute',
              right: '0.5rem', // 8px
              top: '0.5rem', // 8px
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CreatableSelect
                options={supplementOptions}
                value={newSupplement}
                // Trimmed: a created option's value is the raw typed text,
                // and it becomes the row's id (see normalizeSupplementOption).
                onChange={(newValue) => setNewSupplement(normalizeSupplementOption(newValue))}
                placeholder="Search or add a supplement..."
                formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '3rem',
                    borderRadius: '0.75rem',
                    border: `0.0625rem solid ${color.line}`,
                    boxShadow: 'none',
                    '&:hover': {
                      border: `0.0625rem solid ${color.accent}`,
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    boxShadow: '0 0.25rem 1.25rem rgba(0,0,0,0.1)', // 4px 20px
                  }),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Daily Dosage"
                type="number"
                value={newDosage}
                onChange={(e) => setNewDosage(e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 'any' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={newUnit}
                  onChange={(e) => setNewUnit(e.target.value)}
                  label="Unit"
                  sx={{ borderRadius: '0.75rem' }}
                >
                  {Object.entries(config.UNITS).map(([key, label]) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button
            onClick={handleCloseAddModal}
            sx={{ 
              borderRadius: '0.625rem', // 10px
              textTransform: 'none',
              minWidth: '6.25rem', // 100px
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSupplement}
            disabled={!newSupplement || !newDosage || !newUnit}
            sx={{
              ...buttonStyles.primary,
              minWidth: '6.25rem', // 100px
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => handleCloseDrawer(false)}
        sx={inDialog ? { zIndex: theme.zIndex.modal + 1 } : undefined}
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '60%', md: '50%', lg: '40%' },
            maxWidth: '37.5rem', // 600px
            boxShadow: theme.shadows[8],
            borderTopLeftRadius: '1rem',
            borderBottomLeftRadius: '1rem'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: `0.0625rem solid ${theme.palette.divider}`, // Keep 1px
              flexShrink: 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceIcon color="primary" />
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {drawerSupplementName}
              </Typography>
            </Box>
            <IconButton onClick={() => handleCloseDrawer(false)} sx={{ color: theme.palette.grey[500] }}> 
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            ref={drawerContentRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 3,
              pb: { xs: 9, sm: 3 },
              bgcolor: color.surface,
              '&::-webkit-scrollbar': { width: '0.375rem' }, // 6px
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[300], borderRadius: '0.1875rem' }, // 3px
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }
            }}
          >
            {drawerCitations && Array.isArray(drawerCitations) && drawerCitations.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {drawerCitations.map((citation, index) => {
                  return (
                    <Paper
                      key={citation.article_id || index}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '0.75rem',
                        border: `0.0625rem solid ${theme.palette.divider}`, // Keep 1px
                        bgcolor: 'white',
                      }}
                    >
                      {citation.key_finding && (
                         <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              lineHeight: 1.5,
                              mb: 1.5,
                              color: theme.palette.text.primary
                            }}
                         >
                           {citation.key_finding}
                         </Typography>
                      )}

                      <Typography
                         variant="body2"
                         sx={{
                           color: theme.palette.text.secondary,
                           lineHeight: 1.6,
                           mb: 1.5
                         }}
                      >
                        <Box component="span" sx={{ fontWeight: 500 }}>
                          {citation.authors ? citation.authors : 'N/A'}
                        </Box>
                        <Box component="span">
                          {` (${citation.date_published || 'N/A'}). `}
                        </Box>
                        <Box 
                          component="span" 
                          sx={{ fontStyle: 'italic' }}
                          dangerouslySetInnerHTML={{ __html: citation.title || 'Title not available' }} 
                        />
                        {citation.journal ? 
                          <>
                            <Box component="span">{'. '}</Box>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {citation.journal}
                            </Box>
                            {citation.volume && 
                              <Box component="span" sx={{ fontStyle: 'italic' }}>
                                {`, ${citation.volume}`}
                                {citation.issue && `(${citation.issue})`}
                              </Box>
                            }
                            {citation.pages && 
                              <Box component="span">{`, ${citation.pages}`}</Box>
                            }
                            <Box component="span">.</Box>
                          </> : 
                          <Box component="span">.</Box>
                        }
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {hasCitationRelevance(citation) && (
                          <Tooltip
                            title={`Relevance score based on the relevance of this study's findings to ${!!getAuth().currentUser ? 'your specific health goals and profile' : 'general health outcomes'}`}
                            arrow
                          >
                            <Chip
                              icon={<SpeedIcon />}
                              label={`Relevance: ${citationRelevance(citation)}/5`}
                              size="small"
                              variant="outlined"
                              sx={scoreChipStyle}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      {citation.effectiveness_justification && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            mb: 0.5,
                            fontStyle: 'italic',
                            fontSize: '0.75rem', // 12px
                            color: theme.palette.info.dark,
                            bgcolor: theme.palette.info.light + '30',
                            padding: '0.25rem 0.5rem', // 4px 8px
                            borderRadius: '0.25rem', // 4px
                            borderLeft: `0.1875rem solid ${theme.palette.info.main}` // Keep 3px
                          }}
                        >
                           AI Justification: {citation.effectiveness_justification}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                        {citation.population_size !== null && typeof citation.population_size !== 'undefined' && (
                          <Chip
                            icon={<GroupIcon />}
                            label={`n = ${citation.population_size}`}
                            size="small"
                            variant="outlined"
                            sx={attributeChipStyle}
                          />
                        )}
                        {citation.study_type && (
                          <Chip
                            icon={<ArticleIcon />}
                            label={`${citation.study_type}`}
                            size="small"
                            variant="outlined"
                            sx={attributeChipStyle}
                          />
                        )}
                      </Box>

                      {citation.article_id && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                           <Link
                             href={`/supplements/${drawerSupplementId}/research/${citation.article_id}`}
                             onClick={(e) => handleDrawerArticleLinkClickInternal(citation.article_id, e)}
                             variant="body2"
                             sx={{
                               display: 'inline-flex',
                               alignItems: 'center',
                               textDecoration: 'none',
                               color: 'primary.dark',
                               fontSize: '0.8rem',
                               fontWeight: 500,
                               '&:hover': {
                                 color: 'primary.main',
                                 textDecoration: 'underline'
                               }
                             }}
                           >
                             View Study
                             <ChevronRightIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
                           </Link>
                        </Box>
                      )}

                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                No research citations available for this supplement.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Interaction Details Modal */}
      <InteractionDetailsModal
        open={interactionModalOpen}
        onClose={() => setInteractionModalOpen(false)}
        supplementName={selectedInteractionData?.supplementName}
        interactions={selectedInteractionData?.interactions || []}
        primaryInteraction={selectedInteractionData?.primaryInteraction}
      />
    </>
  );
};

export default SupplementsTable;